import axios from "axios";
import { useContext, useEffect, useState } from "react";
import useSWRMutation from "swr/mutation";

import { UploadPhotos } from "@/components/shared/photoRegisterV2";
import { CREW_END_POINTS } from "@/constants/crew/endPoints";
import { OWNER_END_POINTS } from "@/constants/owner/endPoints";
import { AuthContext } from "@/contexts/auth";
import PhotoRegisterProcess from "@/services/common/photo/register";
import { PhotosAtom } from "@/types/common/photos";

type ReqType = "upload" | "delete" | null;

/* 各種写真アップロード（zendeskを除く） */
export const usePhotoRegister = ({
  photoList,
  defaultDisplayCount,
  variant
}: {
  photoList: UploadPhotos[];
  defaultDisplayCount: number;
  variant?: Variant;
}) => {
  const { token } = useContext(AuthContext);
  const [reqType, setReqType] = useState<ReqType>(null);
  const [problemId, setProblemId] = useState<number | null>(null); //クルー報告の場合の問題ID
  const [targetId, setTargetId] = useState<number | null>(null); //クルー報告の場合のターゲットID
  const [photoUploadFiles, setPhotoUploadFiles] = useState<File[] | null>(null); //アップロードする写真ファイル
  const [response, setResponse] = useState<UploadPhotos[] | null>(null); //アップロード結果
  const [delResponse, setDelResponse] = useState<UploadPhotos[] | null>(null); //削除結果
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null); //削除対象の写真ID
  const [showLoadingTipsModal, setShowLoadingTipsModal] = useState(false); //アップロード時間についてのモーダル
  const [tipsTimer, setTipsTimer] = useState<
    ReturnType<typeof setTimeout> | undefined
  >(undefined);
  const [isDeleteComplete, setIsDeleteComplete] = useState(false); //削除完了フラグ
  const [errStep, setErrStep] = useState(""); //エラー発生時のステップ
  const isMultiple = variant === "report" || variant === "comments"; //一括アップロード対応かどうか

  //写真アップロード用のエンドポイントURLを生成
  const createApiPath = (delId: number | null) => {
    setErrStep("Create ApiPath");
    switch (variant) {
      case "report":
        //クルー：作業箇所報告
        return `${CREW_END_POINTS.TASK.SUB_REPORT}/${targetId}/photo${
          delId ? `/${delId}` : "-multiple"
        }`;
      case "comments":
        //クルー：異常箇所報告
        return `${
          CREW_END_POINTS.TASK.SUB_REPORT
        }/${problemId}/comment/${targetId}/photo${
          delId ? `/${delId}` : "-multiple"
        }`;
      case "property":
        //依頼者：物件情報
        return `${OWNER_END_POINTS.VENUE.DETAIL}/${targetId}/photos${
          delId ? `/${delId}` : ""
        }`;
    }
    //TODO: CO-414（今後対応）others / problems / final
  };

  //指定数分の空写真配列を生成
  const createBlankPhoto = (cnt: number) => {
    return Array.from({ length: cnt }).map((_, index) => ({
      id: index,
      path: "",
      previewUrl: "",
      captured_at: "",
      is_aspect_ratio_valid: false,
      bucket: ""
    }));
  };

  //表示用写真リストを生成（指定されたデフォルト表示枚数にする）
  const createDisplayPhotoList = (
    targetId: number | undefined,
    defaultPhotoList: PhotosAtom[] | null,
    uploadList: UploadPhotos[] | null
  ): UploadPhotos[] => {
    //アップ済み写真があればセット
    const filteredPhotoList = defaultPhotoList
      ? (defaultPhotoList
          ?.filter((list) => list.id === targetId)[0]
          ?.photos.map((r) => ({
            ...r,
            id: r.id,
            previewUrl: r.path
          })) as UploadPhotos[]) || []
      : uploadList || [];

    filteredPhotoList.sort((a, b) => (a.position || 0) - (b.position || 0));

    //デフォの表示枚数 - アップ済み写真枚数 = 必要なブランクの数
    const filteredCount = defaultDisplayCount - filteredPhotoList.length;

    //空写真を不足枚数分生成
    if (filteredCount === defaultDisplayCount) {
      return createBlankPhoto(defaultDisplayCount);
    }
    return filteredPhotoList
      .concat(createBlankPhoto(filteredCount))
      .map((r, i) => ({ ...r, id: !r.path ? i : r.id }));
  };

  //UploadPhotos[]をPhotos[]に変換
  const convertPhotoList = (photoList: UploadPhotos[]) => {
    return photoList
      .map((photo) => ({
        ...photo,
        bucket: photo.path,
        position: photo.position || 0
      }))
      .filter((photo) => photo.path);
  };

  //写真アップロード
  const uploadPhoto = async (apiPath: string, files: File[]) => {
    const photoSave = new PhotoRegisterProcess();
    const ud = new FormData();

    // 10秒たったら写真が遅い時のtipsモーダルを表示する
    const timer = setTimeout(() => {
      if (!localStorage.photoUploadTimeIsSlowModalHidden) {
        setShowLoadingTipsModal(true);
      }
    }, 10000);
    setTipsTimer(timer);

    if (isMultiple) {
      // 一括アップロード
      for (let i = 0; i < files.length; i++) {
        setErrStep(`Analyze Photo multiple-${i}`);
        const compFile = await photoSave.analyzePhoto(files[i], true); // 圧縮処理
        const upKey = `images[${i}]`;
        ud.append(`${upKey}[image]`, compFile.file);
        ud.append(
          `${upKey}[captured_at]`,
          compFile.exif?.DateTimeOriginal || ""
        );
        ud.append(`${upKey}[exif]`, JSON.stringify(compFile.exif));
      }
    } else {
      // 単体アップロード
      setErrStep(`Analyze Photo single`);
      const compFile = await photoSave.analyzePhoto(files[0], true); // 圧縮処理
      ud.append("image", compFile.file);
      ud.append("captured_at", compFile.exif?.DateTimeOriginal || "");
      ud.append("exif", JSON.stringify(compFile.exif));
    }

    // リクエスト実行
    const res = await axios.post<UploadPhotos[]>(apiPath, ud, token);
    clearTimeout(timer);
    return res.data;
  };

  //[ POST | DELETE ] リクエスト処理
  //MEMO: Deleteの時は全部返ってくる、uploadの時はアップロードした写真だけ返ってくる
  const { trigger, isMutating, error } = useSWRMutation(
    () => createApiPath(deleteTargetId),
    async (
      apiPath,
      { arg: { method, files } }: { arg: { method: ReqType; files?: File[] } }
    ) => {
      if (method === "upload" && files) {
        //＜アップロード＞
        setErrStep("Upload Photo");
        const result = await uploadPhoto(apiPath, files);
        //pathがブランク(=アップロード失敗またはアップロード前の残骸)の写真を除外
        const upResData = photoList.filter((pt) => pt.path).concat(result);
        const newPhotoList = upResData.map((r) => ({
          ...r,
          previewUrl: r.path
        }));
        setResponse(newPhotoList);
      } else if (method === "delete") {
        //＜削除＞
        setErrStep("Delete Photo");
        setIsDeleteComplete(false);
        const res = await axios.delete<UploadPhotos[]>(apiPath, token);
        setDelResponse(res.data);
        setIsDeleteComplete(true);
      }
      //アップロードファイルをクリア
      setPhotoUploadFiles(null);
    }
  );

  //Hook
  useEffect(() => {
    (async () => {
      try {
        setErrStep("Trigger");
        if (reqType === "upload" && photoUploadFiles) {
          //写真アップロード
          await trigger({ method: reqType, files: photoUploadFiles });
        } else if (reqType === "delete" && deleteTargetId) {
          //写真削除
          await trigger({ method: reqType });
        }
      } catch (error) {
        //TODO: エラーハンドリング
        console.error(`Failed ${reqType} photos`, error);
      } finally {
        clearTimeout(tipsTimer);
      }
    })();

    return () => {
      clearTimeout(tipsTimer);
    };
  }, [reqType, photoUploadFiles, deleteTargetId]);

  return {
    setReqType,
    setProblemId,
    setTargetId,
    setPhotoUploadFiles,
    setDeleteTargetId,
    setShowLoadingTipsModal,
    setIsDeleteComplete,
    deleteTargetId,
    showLoadingTipsModal,
    isMutating,
    uploadPhotoResponse: response,
    uploadPhotoError: error,
    errStep, //エラー発生時のステップ
    isDeleteComplete,
    delResponse,
    createBlankPhoto,
    createDisplayPhotoList,
    convertPhotoList
  };
};

export type Variant =
  | "contact" //お問い合わせフォーム用
  | "report" //クルー報告 作業箇所報告（一括UP対応）
  | "comments" //クルー報告 異常箇所報告（一括UP対応）
  | "problems" //クルー報告 異常チェックリスト
  | "others" //クルー報告 その他申し送り事項
  | "final" //クルー報告 その他報告
  | "property"; //物件情報
